import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';
import GuideArticleView from '../components/GuideArticleView';
import { adminFetch } from '../services/adminApi';
import { appUrl } from '../utils/appPaths';
import { deriveGuideSeoDescription, deriveGuideSeoKeywords, usePageSeo } from '../utils/seo';
import { readGuidePreviewDraft } from '../utils/guidePreviewDraft';

const LoadingState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 340px;
  color: #64748b;
  font-size: 1.1rem;
`;

const ErrorState = styled.div`
  width: min(760px, calc(100% - (var(--site-gutter) * 2)));
  margin: 0 auto;
  padding: 40px 24px;
  margin-top: 32px;
  border-radius: 20px;
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #b91c1c;
  text-align: center;
`;

const GuidePreview = () => {
  const { id } = useParams();
  const location = useLocation();
  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const previewToken = useMemo(() => new URLSearchParams(location.search).get('previewToken') || '', [location.search]);

  usePageSeo({
    title: guide?.title ? `${guide.title} Preview | MicroPinouts` : 'Guide Preview | MicroPinouts',
    description: deriveGuideSeoDescription(guide),
    keywords: deriveGuideSeoKeywords(guide),
    canonicalUrl: guide?.slug ? appUrl(`/guides/${guide.slug}`) : '',
  });

  useEffect(() => {
    const fetchGuidePreview = async () => {
      const draftGuide = previewToken ? readGuidePreviewDraft(previewToken, id) : null;

      if (draftGuide) {
        setGuide(draftGuide);
        setError('');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await adminFetch(`/admin/guides/${id}`);

        if (!response.ok) {
          throw new Error('Failed to load guide preview');
        }

        const data = await response.json();
        setGuide(data || null);
        setError('');
      } catch (fetchError) {
        console.error('Failed to load guide preview:', fetchError);
        setError('Guide preview is unavailable.');
      } finally {
        setLoading(false);
      }
    };

    fetchGuidePreview();
  }, [id, previewToken]);

  if (loading) {
    return <LoadingState>Loading guide preview...</LoadingState>;
  }

  if (error || !guide) {
    return <ErrorState>{error || 'Guide preview is unavailable.'}</ErrorState>;
  }

  const previewLabel = previewToken
    ? 'Current draft preview'
    : guide?.status === 'scheduled'
      ? 'Scheduled guide preview'
      : guide?.published
        ? 'Published guide preview'
        : 'Draft guide preview';

  return (
    <GuideArticleView
      guide={guide}
      previewLabel={previewLabel}
      showEditButton={Boolean(guide?.id)}
      editTarget={guide?.id ? `/admin?tab=guides&editArticle=${guide.id}` : ''}
    />
  );
};

export default GuidePreview;
